import { useMemo } from 'react';
import { Menu } from 'antd';
import { useSelector } from 'react-redux';
import UtilDate from '../../utils/UtilDate';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import ApiStorage from '../../redux/ApiStorage';
import UtilObject from '../../utils/UtilObject';
import MyLink from '../../components/myLink/MyLink';
import MyBadge from '../../components/badge/MyBadge';
import useWindowSize from '../../hooks/useWindowSize';
import OrdersActions from '../../actions/OrdersActions';
import MyLucideIcon from '../../components/myIcon/MyLucideIcon';
import {
  Users,
  MapPin,
  Wallet,
  Package,
  Banknote,
  UserCog,
  BarChart3,
  Warehouse,
  MapPinned,
  ShoppingCart,
  LayoutDashboard,
} from 'lucide-react';
import './SideBarMenu.scss';

const SideBarMenu = ({ onSelect }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { isMobile } = useWindowSize();

  const badgeCountOrder = useSelector((state) => {
    return state.badgeCountOrder;
  });

  const badgeCountPayment = useSelector((state) => {
    return state.badgeCountPayment;
  });

  const badgeCountCustomer = useSelector((state) => {
    return state.badgeCountCustomer;
  });

  const lang = useSelector((state) => {
    return state.lang;
  });

  const useStoreSystem = useSelector((state) => {
    return state.currentUser?.useStoreSystem;
  });

  let svgSize = 20;

  const badgeOffset = useMemo(() => {
    return isMobile ? [6, 4] : [6, -3];
  }, [isMobile]);

  const items = useMemo(() => {
    return [
      {
        key: `/${lang}/dashboard`,
        className: 'side_menu_item',
        label: (
          <MyLink path="/dashboard" state={UtilObject.EMPTY_OBJECT}>
            {t('WORD_DASHBOARD')}
          </MyLink>
        ),
        icon: <MyLucideIcon icon={LayoutDashboard} size={svgSize} />,
      },
      {
        type: 'group',
      },
      {
        key: `/${lang}/order/list`,
        className: 'side_menu_item',
        label: (
          <MyLink
            path="/order/list"
            state={UtilObject.EMPTY_OBJECT}
            onClick={() => {
              ApiStorage.setOrderPageFilters(UtilObject.EMPTY_OBJECT);
              if (badgeCountOrder) {
                OrdersActions.clearBadgeCountOrder();
              }
            }}
          >
            {t('WORD_ORDERS')}
          </MyLink>
        ),
        icon: (
          <MyBadge
            count={badgeCountOrder}
            offset={badgeOffset}
            size="small"
            className="side_bar_menu_badge"
          >
            <MyLucideIcon icon={ShoppingCart} size={svgSize} />
          </MyBadge>
        ),
      },
      {
        key: `/${lang}/payment/list`,
        className: 'side_menu_item',
        label: (
          <MyLink
            path="/payment/list"
            state={UtilObject.EMPTY_OBJECT}
            onClick={() => {
              ApiStorage.setPaymentPageFilters(UtilObject.EMPTY_OBJECT);
              if (badgeCountPayment) {
                OrdersActions.clearBadgeCountPayment();
              }
            }}
          >
            {t('WORD_PAYMENTS')}
          </MyLink>
        ),
        icon: (
          <MyBadge
            count={badgeCountPayment}
            offset={badgeOffset}
            size="small"
            className="side_bar_menu_badge"
          >
            <MyLucideIcon icon={Wallet} size={svgSize} />
          </MyBadge>
        ),
      },
      {
        key: `/${lang}/visit/list`,
        className: 'side_menu_item',
        label: (
          <MyLink
            path="/visit/list"
            state={UtilObject.EMPTY_OBJECT}
            onClick={() => {
              ApiStorage.setVisitPageFilters(UtilObject.EMPTY_OBJECT);
            }}
          >
            {t('WORD_VISITS')}
          </MyLink>
        ),
        icon: <MyLucideIcon icon={MapPin} size={svgSize} />,
      },
      {
        type: 'group',
      },
      {
        key: `/${lang}/product/list`,
        className: 'side_menu_item',
        label: (
          <MyLink path="/product/list" state={{ resetProductList: true }}>
            {t('WORD_PRODUCTS')}
          </MyLink>
        ),
        icon: <MyLucideIcon icon={Package} size={svgSize} />,
      },
      {
        key: `/${lang}/customer/list`,
        className: 'side_menu_item',
        label: (
          <MyLink
            path="/customer/list"
            state={UtilObject.EMPTY_OBJECT}
            onClick={() => {
              ApiStorage.setCustomerPageFilters(UtilObject.EMPTY_OBJECT);
              ApiStorage.setCustomerStatementFilters(UtilObject.EMPTY_OBJECT);
            }}
          >
            {t('WORD_CUSTOMERS')}
          </MyLink>
        ),
        icon: (
          <MyBadge
            count={badgeCountCustomer}
            offset={badgeOffset}
            size="small"
            className="side_bar_menu_badge"
          >
            <MyLucideIcon icon={Users} size={svgSize} />
          </MyBadge>
        ),
      },
      {
        key: `/${lang}/debit/list`,
        className: 'side_menu_item',
        label: (
          <MyLink
            path="/debit/list"
            state={UtilObject.EMPTY_OBJECT}
            onClick={() => {
              ApiStorage.setDebitPageFilters(UtilObject.EMPTY_OBJECT);
            }}
          >
            {t('WORD_DEBTS')}
          </MyLink>
        ),
        icon: <MyLucideIcon icon={Banknote} size={svgSize} />,
      },
      {
        key: `/${lang}/analytics/orders`,
        className: 'side_menu_item',
        label: (
          <MyLink
            path="/analytics/orders"
            state={UtilObject.EMPTY_OBJECT}
            onClick={() => {
              ApiStorage.setAnalyticsFilters({
                date: 'THIS_MONTH',
                dateFrom: UtilDate.getThisMonthStart(),
                dateTo: UtilDate.getThisMonthEnd(),
              });
            }}
          >
            {t('WORD_ANALYTICS')}
          </MyLink>
        ),
        icon: <MyLucideIcon icon={BarChart3} size={svgSize} />,
      },
      {
        key: `/${lang}/gps`,
        className: 'side_menu_item',
        label: (
          <MyLink path="/gps" state={UtilObject.EMPTY_OBJECT}>
            {t('WORD_GPS')}
          </MyLink>
        ),
        icon: <MyLucideIcon icon={MapPinned} size={svgSize} />,
      },
      {
        key: `/${lang}/user/list`,
        className: 'side_menu_item',
        label: (
          <MyLink
            path="/user/list"
            state={UtilObject.EMPTY_OBJECT}
            onClick={() => {
              ApiStorage.setUserPageFilters(UtilObject.EMPTY_OBJECT);
            }}
          >
            {t('WORD_SALES_TEAM')}
          </MyLink>
        ),
        icon: <MyLucideIcon icon={UserCog} size={svgSize} />,
      },
      useStoreSystem && {
        key: `/${lang}/inventory/list`,
        className: 'side_menu_item',
        label: (
          <MyLink
            path="/inventory/list"
            state={UtilObject.EMPTY_OBJECT}
            onClick={() => {
              ApiStorage.setProductQuantitiesFilters(UtilObject.EMPTY_OBJECT);
            }}
          >
            {t('WORD_STORE')}
          </MyLink>
        ),
        icon: <MyLucideIcon icon={Warehouse} size={svgSize} />,
      },
    ];
  }, [
    t,
    lang,
    svgSize,
    badgeOffset,
    useStoreSystem,
    badgeCountOrder,
    badgeCountPayment,
    badgeCountCustomer,
  ]);

  const getSelectedMenuKey = (pathname) => {
    if (pathname.startsWith(`/${lang}/analytics/`)) {
      return `/${lang}/analytics/orders`;
    }

    const menuKeys = [
      `/${lang}/dashboard`,
      `/${lang}/order/list`,
      `/${lang}/payment/list`,
      `/${lang}/visit/list`,
      `/${lang}/product/list`,
      `/${lang}/customer/list`,
      `/${lang}/debit/list`,
      `/${lang}/analytics/orders`,
      `/${lang}/gps`,
      `/${lang}/user/list`,
      `/${lang}/device/list`,
      `/${lang}/branch/list`,
      `/${lang}/policy/list`,
      useStoreSystem && `/${lang}/inventory/list`,
    ].filter(Boolean);
    return (
      menuKeys.find((key) => pathname.startsWith(key.replace('/list', ''))) ||
      pathname
    );
  };

  return (
    <Menu
      className="main_menu no-print"
      defaultSelectedKeys={['1']}
      items={items}
      selectedKeys={[getSelectedMenuKey(location.pathname)]}
      onSelect={(menu) => {
        onSelect && onSelect(menu);
      }}
    />
  );
};

export default SideBarMenu;
